import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { LoadingSpinner } from "./loading-spinner";

const disabledMutedSolid =
  "disabled:bg-disabled-button disabled:text-disabled-button-foreground disabled:shadow-none disabled:hover:bg-disabled-button disabled:hover:text-disabled-button-foreground";

const disabledMutedGhost =
  "disabled:bg-transparent disabled:text-disabled-button-foreground disabled:shadow-none disabled:hover:bg-transparent disabled:hover:text-disabled-button-foreground";

const disabledMutedLink =
  "disabled:bg-transparent disabled:text-disabled-button-foreground disabled:shadow-none disabled:no-underline disabled:hover:bg-transparent disabled:hover:text-disabled-button-foreground";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default: cn("bg-primary text-primary-foreground hover:bg-primary/90", disabledMutedSolid),
        destructive: cn("bg-destructive text-destructive-foreground hover:bg-destructive/90", disabledMutedSolid),
        outline: cn(
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
          "disabled:border-input",
          disabledMutedSolid,
        ),
        secondary: cn("bg-secondary text-secondary-foreground hover:bg-secondary/80", disabledMutedSolid),
        ghost: cn("hover:bg-accent hover:text-accent-foreground", disabledMutedGhost),
        link: cn("text-primary underline-offset-4 hover:underline", disabledMutedLink),
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = disabled || loading;

    // Determine spinner size based on button size
    const spinnerSize = size === "icon" ? "sm" : size === "sm" ? "sm" : size === "lg" ? "md" : "sm";

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <LoadingSpinner size={spinnerSize} className="w-4 h-4" />
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
